import { useState, useEffect, useCallback } from 'react'
import {
  Mail, CalendarDays, HardDrive, Bell, GitBranch, Plus, Trash2, Copy, Check, Send,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  integrationsBreadthService as svc,
  Mailbox, CalendarConnection, StorageConnection, ChannelConfig,
} from '../services/integrationsBreadth'
import { getErrorMessage } from '../utils/apiTransformers'

type Tab = 'git' | 'mailboxes' | 'calendar' | 'storage' | 'channels'

export default function IntegrationBreadth() {
  const [tab, setTab] = useState<Tab>('git')

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Integration Breadth</h1>
        <p className="text-gray-500">
          Connect the platform to the rest of your stack — git, email, calendars,
          document storage, and notification channels. Everything here is optional;
          the app works fully with nothing connected.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {([
          ['git', 'Git Ref Prefix', GitBranch],
          ['mailboxes', 'Email-in', Mail],
          ['calendar', 'Calendar', CalendarDays],
          ['storage', 'Storage', HardDrive],
          ['channels', 'Channels', Bell],
        ] as [Tab, string, LucideIcon][]).map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === 'git' && <GitPrefixPanel />}
      {tab === 'mailboxes' && <MailboxPanel />}
      {tab === 'calendar' && <CalendarPanel />}
      {tab === 'storage' && <StoragePanel />}
      {tab === 'channels' && <ChannelPanel />}
    </div>
  )
}

// ── A10.5 Git ref prefix ──────────────────────────────────────────────────────
function GitPrefixPanel() {
  const [prefix, setPrefix] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    svc.getRefPrefix()
      .then(d => setPrefix(d.prefix))
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      const d = await svc.setRefPrefix(prefix)
      setPrefix(d.prefix)
      toast.success(`Task references now use ${d.prefix}-<id>`)
    } catch (e) { toast.error(getErrorMessage(e)) }
    finally { setSaving(false) }
  }

  if (loading) return <p className="text-gray-500">Loading…</p>

  return (
    <div className="max-w-xl">
      <p className="text-sm text-gray-600 mb-3">
        When a GitHub / GitLab / Bitbucket pull-request title contains
        <span className="font-mono"> {prefix || 'KARV'}-42</span>, the merge is
        linked to task #42 and (optionally) closes it.
      </p>
      <div className="flex items-center gap-2">
        <input
          value={prefix}
          onChange={e => setPrefix(e.target.value.toUpperCase())}
          maxLength={16}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm font-mono uppercase"
          placeholder="KARV"
        />
        <span className="text-gray-400 font-mono">-&lt;id&gt;</span>
        <button onClick={save} disabled={saving}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">
          Save
        </button>
      </div>
    </div>
  )
}

// ── A10.2 Mailboxes ────────────────────────────────────────────────────────────
function MailboxPanel() {
  const [items, setItems] = useState<Mailbox[]>([])
  const [loading, setLoading] = useState(true)
  const [projectId, setProjectId] = useState('')
  const [action, setAction] = useState<'comment' | 'task'>('comment')
  const [copied, setCopied] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    svc.listMailboxes()
      .then(setItems)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const create = async () => {
    const pid = parseInt(projectId, 10)
    if (!pid) { toast.error('Enter a project ID'); return }
    try {
      await svc.createMailbox(pid, action)
      setProjectId(''); load()
      toast.success('Mailbox created')
    } catch (e) { toast.error(getErrorMessage(e)) }
  }

  const remove = async (id: number) => {
    try { await svc.deleteMailbox(id); load() }
    catch (e) { toast.error(getErrorMessage(e)) }
  }

  const copy = (addr: string, id: number) => {
    navigator.clipboard.writeText(addr)
    setCopied(id)
    setTimeout(() => setCopied(null), 1500)
  }

  if (loading) return <p className="text-gray-500">Loading…</p>

  return (
    <div>
      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Project ID</label>
          <input value={projectId} onChange={e => setProjectId(e.target.value)}
                 className="border border-gray-300 rounded px-3 py-1.5 text-sm w-28" placeholder="e.g. 7" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">On reply</label>
          <select value={action} onChange={e => setAction(e.target.value as typeof action)}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm">
            <option value="comment">Add comment</option>
            <option value="task">Create task</option>
          </select>
        </div>
        <button onClick={create} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm">
          <Plus className="w-4 h-4" /> New Mailbox
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-gray-500">No mailboxes yet. Emails to a project mailbox become comments or tasks.</p>
      ) : (
        <div className="space-y-2">
          {items.map(m => (
            <div key={m.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
              <div>
                <span className="font-mono text-sm text-gray-900">{m.address}</span>
                <span className="ml-2 text-xs text-gray-500">→ {m.default_action} · project {m.project_id}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => m.address && copy(m.address, m.id)} className="text-gray-400 hover:text-blue-600">
                  {copied === m.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(m.id)} className="text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── A10.3 Calendar ──────────────────────────────────────────────────────────────
function CalendarPanel() {
  const [items, setItems] = useState<CalendarConnection[]>([])
  const [feedUrl, setFeedUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([svc.listCalendarConnections(), svc.getCalendarFeedUrl()])
      .then(([conns, feed]) => { setItems(conns); setFeedUrl(feed.url) })
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const remove = async (id: number) => {
    try { await svc.deleteCalendarConnection(id); load() }
    catch (e) { toast.error(getErrorMessage(e)) }
  }

  if (loading) return <p className="text-gray-500">Loading…</p>

  const fullFeed = `${window.location.origin}${feedUrl}`

  return (
    <div>
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
        <div className="text-blue-800 font-semibold mb-1">Subscribe to deadlines (no setup needed)</div>
        <p className="text-sm text-gray-700 mb-2">
          Add this read-only ICS URL to Google Calendar, Apple Calendar, or Outlook
          to see all task deadlines.
        </p>
        <div className="flex items-center gap-2">
          <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 truncate flex-1">{fullFeed}</code>
          <button onClick={() => { navigator.clipboard.writeText(fullFeed); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
                  className="text-gray-500 hover:text-blue-600">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2">Two-way connections (Google / Outlook)</h3>
      {items.length === 0 ? (
        <p className="text-gray-500">No calendar connected. Authorise one to push task due-dates as events.</p>
      ) : (
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
              <span className="text-sm text-gray-900 capitalize">{c.provider} · {c.sync_direction}</span>
              <button onClick={() => remove(c.id)} className="text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── A10.4 Storage ─────────────────────────────────────────────────────────────
function StoragePanel() {
  const [items, setItems] = useState<StorageConnection[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    svc.listStorageConnections()
      .then(setItems)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const remove = async (id: number) => {
    try { await svc.deleteStorageConnection(id); load() }
    catch (e) { toast.error(getErrorMessage(e)) }
  }

  if (loading) return <p className="text-gray-500">Loading…</p>

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        Connect Google Drive, OneDrive/SharePoint, or Dropbox as an attachment
        backend. The default connection receives new uploads; without one, files
        use local disk or S3.
      </p>
      {items.length === 0 ? (
        <p className="text-gray-500">No storage providers connected.</p>
      ) : (
        <div className="space-y-2">
          {items.map(s => (
            <div key={s.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
              <span className="text-sm text-gray-900 capitalize">
                {s.provider}{s.is_default && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">default</span>}
              </span>
              <button onClick={() => remove(s.id)} className="text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── A10.6 Channels ──────────────────────────────────────────────────────────────
function ChannelPanel() {
  const [items, setItems] = useState<ChannelConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [channel, setChannel] = useState<'sms' | 'push' | 'webhook'>('sms')
  const [target, setTarget] = useState('')
  const [secret, setSecret] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    svc.listChannels()
      .then(setItems)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!target.trim()) { toast.error('Enter a target'); return }
    try {
      await svc.createChannel({ channel, target: target.trim(), secret: secret || undefined })
      setTarget(''); setSecret(''); load()
      toast.success('Channel added')
    } catch (e) { toast.error(getErrorMessage(e)) }
  }

  const test = async (id: number) => {
    try {
      const r = await svc.testChannel(id)
      if (r.ok) toast.success('Test sent')
      else if (r.skipped) toast.warning(`Channel not configured: ${r.detail}`)
      else toast.error(`Failed: ${r.detail}`)
    } catch (e) { toast.error(getErrorMessage(e)) }
  }

  const remove = async (id: number) => {
    try { await svc.deleteChannel(id); load() }
    catch (e) { toast.error(getErrorMessage(e)) }
  }

  const targetLabel = channel === 'sms' ? 'Phone number'
    : channel === 'push' ? 'Device token' : 'Webhook URL'

  if (loading) return <p className="text-gray-500">Loading…</p>

  return (
    <div>
      <div className="flex items-end gap-2 mb-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Channel</label>
          <select value={channel} onChange={e => setChannel(e.target.value as typeof channel)}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm">
            <option value="sms">SMS (Twilio)</option>
            <option value="push">Mobile push</option>
            <option value="webhook">Webhook</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{targetLabel}</label>
          <input value={target} onChange={e => setTarget(e.target.value)}
                 className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64" />
        </div>
        {channel === 'webhook' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">HMAC secret (optional)</label>
            <input value={secret} onChange={e => setSecret(e.target.value)}
                   className="border border-gray-300 rounded px-3 py-1.5 text-sm w-48" />
          </div>
        )}
        <button onClick={create} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-gray-500">No extra channels. Add SMS, push, or a webhook to receive notifications beyond in-app and email.</p>
      ) : (
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
              <span className="text-sm text-gray-900 uppercase">{c.channel}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => test(c.id)} className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm">
                  <Send className="w-4 h-4" /> Test
                </button>
                <button onClick={() => remove(c.id)} className="text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
